import React, { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  BarChart3,
  Building2,
  CreditCard,
  FileText,
  Info,
  Landmark,
  Loader2,
  Settings2,
} from 'lucide-react'
import { MainLayout } from '@/components/layout/MainLayout'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/hooks/use-toast'
import { InvoiceApplicationDialog } from '@/components/setup/InvoiceApplicationDialog'
import {
  CompanyCreditCard,
  PaymentFlowSteps,
  StandardFundingCard,
} from '@/components/payments/PaymentOptions'
import { useAuth } from '@/hooks/useAuth'
import { cn } from '@/lib/utils'
import {
  useConfirmPaymentMethodSetupMutation,
  useGetBillingQuery,
  useStartPaymentMethodSetupMutation,
  type BillingInfo,
} from '@/redux/marketerApi'

const card = 'rounded-2xl border border-border bg-card p-5 shadow-sm'
const dollars = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: cents % 100 ? 2 : 0 })}`
const day = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

const Badge: React.FC<{ tone: 'green' | 'grey' | 'amber' | 'red'; children: React.ReactNode }> = ({
  tone,
  children,
}) => (
  <span
    className={cn(
      'inline-flex rounded-md px-2.5 py-1 text-sm font-semibold',
      tone === 'green' && 'bg-[#E8F9D2] text-[#2F5E06]',
      tone === 'grey' && 'bg-[#F1F5F9] text-[#475569]',
      tone === 'amber' && 'bg-[#FEF3D7] text-[#92400E]',
      tone === 'red' && 'bg-[#FEE2E2] text-[#B91C1C]',
    )}
  >
    {children}
  </span>
)

const Summary: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({
  icon,
  title,
  children,
}) => (
  <section className={cn(card, 'flex gap-4')}>
    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#F1F5F0] text-foreground">
      {icon}
    </span>
    <div className="min-w-0 space-y-1">
      <p className="font-semibold text-foreground">{title}</p>
      {children}
    </div>
  </section>
)

const methodLabel = (m: NonNullable<BillingInfo['paymentMethod']>) =>
  m.type === 'card'
    ? `${(m.brand ?? 'Card').replace(/^\w/, (c) => c.toUpperCase())} ending ${m.last4 ?? '••••'}`
    : `${m.bankName ?? 'Bank account'} ending ${m.last4 ?? '••••'}`

const STATUS: Record<string, { label: string; tone: 'green' | 'grey' | 'amber' | 'red' }> = {
  paid: { label: 'Paid', tone: 'green' },
  due: { label: 'Due', tone: 'amber' },
  awaiting_funding: { label: 'Awaiting funding', tone: 'grey' },
}

/**
 * Payments (account menu → Payments and billing): how the brand pays, its saved card or bank
 * account, company credit, and everything it has paid or owes.
 */
const Payments: React.FC = () => {
  const { toast } = useToast()
  const { marketer } = useAuth()
  const org = (marketer as any)?.org
  const [params, setParams] = useSearchParams()
  const { data: billing, isLoading } = useGetBillingQuery()
  const [startSetup, { isLoading: starting, originalArgs }] = useStartPaymentMethodSetupMutation()
  const [confirmSetup] = useConfirmPaymentMethodSetupMutation()
  const [applyOpen, setApplyOpen] = useState(false)
  const [learnMore, setLearnMore] = useState(false)
  const confirmed = useRef(false)

  // Back from Stripe: save the new card or bank account
  useEffect(() => {
    const setup = params.get('setup')
    const sessionId = params.get('session_id')
    if (!setup || confirmed.current) return
    confirmed.current = true
    const done = () => setParams({}, { replace: true })
    if (setup === 'done' && sessionId) {
      confirmSetup(sessionId)
        .unwrap()
        .then(() =>
          toast({ title: 'Payment method saved', description: 'Deals will be funded with it.' }),
        )
        .catch((err: any) =>
          toast({
            title: "Couldn't save the payment method",
            description: err?.data?.error ?? 'Try again.',
            variant: 'destructive',
          }),
        )
        .finally(done)
    } else {
      toast({ title: 'No changes made' })
      done()
    }
  }, [params, setParams, confirmSetup, toast])

  const add = async (kind: 'card' | 'bank') => {
    try {
      const { url } = await startSetup({ kind }).unwrap()
      window.location.assign(url)
    } catch (err: any) {
      toast({
        title: err?.status === 503 ? 'Payments are coming soon' : "Couldn't open Stripe",
        description: err?.data?.error ?? 'Try again in a moment.',
        variant: err?.status === 503 ? undefined : 'destructive',
      })
    }
  }

  if (isLoading || !billing) {
    return (
      <MainLayout>
        <div className="mx-auto max-w-6xl space-y-4 p-4 sm:p-8">
          <Skeleton className="h-12 w-72" />
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-80 w-full" />
        </div>
      </MainLayout>
    )
  }

  const invoiced = billing.method === 'invoice'
  const app = billing.application
  const creditStatus = invoiced
    ? { label: 'Approved', tone: 'green' as const }
    : app?.status === 'pending'
      ? { label: 'Under review', tone: 'amber' as const }
      : app?.status === 'declined'
        ? { label: 'Not approved', tone: 'red' as const }
        : { label: 'Not applied', tone: 'grey' as const }

  return (
    <MainLayout>
      <div className="mx-auto max-w-6xl space-y-5 p-4 sm:p-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Manage Payments</h1>
          <p className="mt-1 text-lg text-muted-foreground">
            Set how your company pays, review funding status, and manage billing for accepted
            deals.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <Summary icon={<CreditCard className="h-6 w-6" />} title="Current setup">
            <Badge tone="green">
              {invoiced
                ? `Invoice billing, net ${billing.termsDays ?? 30}`
                : 'Standard funding active'}
            </Badge>
            <p className="text-sm text-muted-foreground">
              {invoiced
                ? 'Athios invoices you as deliverables go out.'
                : 'Fund deals as they are accepted.'}
            </p>
          </Summary>
          <Summary icon={<Building2 className="h-6 w-6" />} title="Company credit">
            <Badge tone={creditStatus.tone}>{creditStatus.label}</Badge>
            <p className="text-sm text-muted-foreground">
              {invoiced && billing.creditLimitCents
                ? `Spending limit ${dollars(billing.creditLimitCents)}.`
                : 'Apply for invoice billing and a spending limit.'}
            </p>
          </Summary>
          <Summary icon={<BarChart3 className="h-6 w-6" />} title="Payment activity">
            <p className="text-xl font-bold tabular-nums text-foreground">
              {billing.activity.pending} pending / {billing.activity.paid} paid
            </p>
            <p className="text-sm text-muted-foreground">
              {billing.activity.items.length
                ? 'Across your accepted deals.'
                : 'No funded deals yet.'}
            </p>
          </Summary>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <StandardFundingCard
            compact
            points={[
              'No bank connection required during onboarding',
              'When a deal is accepted, Athios prompts you to fund it before paid work starts',
              'Additional charges like appearances, bonuses, and licensing extensions are added as they happen',
            ]}
          >
            <div className="border-t border-border pt-5">
              <p className="mb-3 font-semibold text-foreground">Preferred payment method</p>
              <div className="flex items-center gap-4 rounded-xl bg-[#F6F7F5] px-4 py-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-card text-foreground">
                  {billing.paymentMethod?.type === 'us_bank_account' ? (
                    <Landmark className="h-5 w-5" />
                  ) : (
                    <CreditCard className="h-5 w-5" />
                  )}
                </span>
                {billing.paymentMethod ? (
                  <div>
                    <p className="font-semibold text-foreground">
                      {methodLabel(billing.paymentMethod)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Default for funding deals
                      {billing.paymentMethod.type === 'card' && billing.paymentMethod.expMonth
                        ? ` · expires ${billing.paymentMethod.expMonth}/${String(billing.paymentMethod.expYear).slice(-2)}`
                        : ''}
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="font-semibold text-foreground">
                      No default payment method added yet
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Add a card or bank account to make funding faster.
                    </p>
                  </div>
                )}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {(['card', 'bank'] as const).map((kind) => (
                  <button
                    key={kind}
                    type="button"
                    disabled={starting}
                    onClick={() => add(kind)}
                    className={cn(
                      'inline-flex h-11 items-center gap-2 rounded-xl px-5 font-semibold disabled:opacity-60',
                      kind === 'card'
                        ? 'bg-[#C0FD72] text-[#0F172A] hover:bg-[#B2F25E]'
                        : 'border border-border bg-card text-foreground hover:bg-muted',
                    )}
                  >
                    {starting && originalArgs?.kind === kind ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : null}
                    {billing.paymentMethod
                      ? kind === 'card'
                        ? 'Use a different card'
                        : 'Use a bank account'
                      : kind === 'card'
                        ? 'Add card'
                        : 'Add bank account'}
                  </button>
                ))}
              </div>
              <p className="mt-4 flex gap-2 rounded-xl border border-[#BFD7FB] bg-[#EFF5FF] px-4 py-3 text-sm text-foreground">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#2563EB]" />
                Funding happens at checkout when a deal is confirmed, like a secure order. Card and
                bank details are held by Stripe, never by Athios.
              </p>
            </div>
          </StandardFundingCard>

          <CompanyCreditCard compact>
            <dl className="divide-y divide-border rounded-xl border border-border bg-[#F9FAF8] px-4">
              <div className="flex items-center justify-between py-3 text-sm">
                <dt className="text-foreground">Application status</dt>
                <dd>
                  <Badge tone={creditStatus.tone}>{creditStatus.label}</Badge>
                </dd>
              </div>
              <div className="flex items-center justify-between py-3 text-sm">
                <dt className="text-foreground">Spending limit</dt>
                <dd className="font-medium text-foreground">
                  {invoiced && billing.creditLimitCents ? dollars(billing.creditLimitCents) : '—'}
                </dd>
              </div>
              <div className="flex items-center justify-between py-3 text-sm">
                <dt className="text-foreground">Terms</dt>
                <dd className="font-medium text-foreground">
                  {invoiced
                    ? `Net ${billing.termsDays ?? 30}`
                    : app?.status === 'pending'
                      ? `Net ${app.termsRequestedDays} requested`
                      : '—'}
                </dd>
              </div>
            </dl>
            {app?.status === 'declined' && app.decisionNote ? (
              <p className="mt-3 text-sm text-muted-foreground">
                Last decision: {app.decisionNote}
              </p>
            ) : null}
            <div className="mt-4 flex flex-wrap gap-2">
              {!invoiced && app?.status !== 'pending' ? (
                <button
                  type="button"
                  onClick={() => setApplyOpen(true)}
                  className="h-11 rounded-xl bg-[#C0FD72] px-5 font-semibold text-[#0F172A] hover:bg-[#B2F25E]"
                >
                  {app?.status === 'declined' ? 'Apply again' : 'Apply for credit'}
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setLearnMore((v) => !v)}
                aria-expanded={learnMore}
                className="h-11 rounded-xl border border-border bg-card px-5 font-semibold text-foreground hover:bg-muted"
              >
                {learnMore ? 'Show less' : 'Learn more'}
              </button>
            </div>
            {learnMore ? (
              <p className="mt-3 text-sm text-muted-foreground">
                With company credit, Athios invoices you on net terms as deliverables go out
                instead of funding each deal up front. Tell us your legal name, billing contact and
                address, expected annual spend and the terms you need; purchase orders and vendor
                setup are supported. The Athios team reviews it, usually within two business days,
                and sets a spending limit.
              </p>
            ) : null}
          </CompanyCreditCard>
        </div>

        <section className={card}>
          <h2 className="mb-4 flex items-center gap-3 text-lg font-bold text-foreground">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#F1F5F0]">
              <BarChart3 className="h-5 w-5" />
            </span>
            Billing activity
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead>
                <tr className="bg-[#F6F7F5] text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2.5 font-semibold">Date</th>
                  <th className="px-4 py-2.5 font-semibold">Type</th>
                  <th className="px-4 py-2.5 font-semibold">Campaign</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Amount</th>
                  <th className="px-4 py-2.5 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {billing.activity.items.map((i, n) => (
                  <tr key={`${i.date}-${n}`}>
                    <td className="whitespace-nowrap px-4 py-3 text-foreground">{day(i.date)}</td>
                    <td className="px-4 py-3 text-foreground">
                      {i.type === 'Invoice'
                        ? `Invoice${i.reference ? ` ${i.reference}` : ''}`
                        : 'Accepted deal'}
                    </td>
                    <td className="px-4 py-3 text-foreground">
                      {i.campaign ?? '—'}
                      {i.athlete ? (
                        <span className="block text-xs text-muted-foreground">{i.athlete}</span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold tabular-nums text-foreground">
                      {dollars(i.amountCents)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-2">
                        <Badge tone={STATUS[i.status].tone}>{STATUS[i.status].label}</Badge>
                        {i.payUrl ? (
                          <a
                            href={i.payUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="font-semibold text-[#3F7A0A] hover:underline"
                          >
                            Pay
                          </a>
                        ) : null}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!billing.activity.items.length ? (
            <div className="py-10 text-center">
              <FileText className="mx-auto h-7 w-7 text-muted-foreground" />
              <p className="mt-2 font-semibold text-foreground">No payment activity yet</p>
              <p className="text-sm text-muted-foreground">
                Accepted deals, funded orders, and future invoice activity will appear here.
              </p>
            </div>
          ) : null}
        </section>

        <section className={card}>
          <h2 className="mb-4 flex items-center gap-3 text-lg font-bold text-foreground">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#F1F5F0]">
              <Settings2 className="h-5 w-5" />
            </span>
            How payment works
          </h2>
          <PaymentFlowSteps />
          <p className="mt-4 text-sm text-muted-foreground">
            Athios collects payment before paid work starts, then tracks all additional charges as
            campaign activity progresses.
          </p>
        </section>
      </div>

      <InvoiceApplicationDialog
        open={applyOpen}
        onOpenChange={setApplyOpen}
        defaultLegalName={org?.name}
        defaultContactName={(marketer as any)?.name}
        defaultEmail={(marketer as any)?.email}
      />
    </MainLayout>
  )
}

export default Payments
